import { useEffect, useRef, useState } from 'react';
import { Copy } from 'lucide-react';
import { Label } from '@higgins/ui';
import { areaPhotoOf, shownPhotos, type PhotoSources } from '../lib/area-photos';
import { patchFields, type DeviceRecord, type Fields } from '../lib/device-db';
import { useDeferredSave } from '../lib/use-deferred-save';
import { parseSurfaces, serialiseSurfaces, type SurfacesDoc } from '@higgins/m2q-pricing';
import { AreaBuilding } from './area-building';
import { AreaPhotoFlag, AreaPhotoTile } from './area-photo';
import { siteWash } from '../lib/site-wash';
import { AssessPanel } from './assess-panel';
import { ConflictChoices } from './conflict-choices';
import { NumberField } from './number-field';
import { PhotoStrip } from './photo-strip';
import { RemoveArea } from './remove-area';
import { SavedTextField } from './saved-text-field';
import { SiteRequirements } from './site-requirements';
import { SurfaceList } from './surface-list';
import { storedHours } from './surface-format';
import { ErrorMessage, Notice } from './message';

const SIDES = ['Internal', 'External'] as const;
/** Which of the job page's tabs this area is shown under (the Condition tab has its own panel). */
export type AreaMode = 'measure' | 'assess';

function text(value: Fields[string]): string {
  return typeof value === 'string' ? value : '';
}

const num = (value: Fields[string]): number | null => (typeof value === 'number' ? value : null);

/** The area's surfaces, or why they can't be shown (a newer app version saved them). */
function readSurfaces(value: Fields[string]): { doc: SurfacesDoc } | { error: string } {
  try {
    return { doc: parseSurfaces(typeof value === 'string' ? value : null) };
  } catch (err) {
    return { error: err instanceof Error ? err.message : String(err) };
  }
}

/** Changed columns of one area, waiting to be saved. */
interface AreaChanges {
  id: string;
  changes: Fields;
}

const joinChanges = (older: AreaChanges, newer: AreaChanges): AreaChanges => ({ id: newer.id, changes: { ...older.changes, ...newer.changes } });

/**
 * Edit one area. Every change is saved to the device a moment after typing
 * stops — never kept only on screen — and queued to sync. Only the columns
 * changed here are saved, merged into the stored copy: the rest of the
 * screen's copy may be older (a photo's upload adds to hig_photos meanwhile).
 */
export function AreaEditor({
  area,
  mode,
  photoSources,
  onDuplicate,
  onRemove,
  onAssessed,
}: {
  area: DeviceRecord;
  mode: AreaMode;
  /** The job's photos, read once by the page. */
  photoSources: PhotoSources;
  onDuplicate: () => Promise<void>;
  onRemove: () => Promise<void>;
  /** PM hours were entered: the measure moves on to Assessing (never back; measure-status.ts). */
  onAssessed: () => void;
}) {
  const [draft, setDraft] = useState<Fields>(area.fields);
  // Duplicate area or Remove area didn't work: shown under their buttons.
  const [actionError, setActionError] = useState<string | null>(null);
  // The draft as last changed, ahead of the next render: a box saving as the app hides reads it straight away.
  const latestDraft = useRef(draft);
  const saver = useDeferredSave((pending: AreaChanges) => patchFields(pending.id, pending.changes), joinChanges);
  // Its photo rows, the older list from CRM and this device's own (area-photos.ts).
  const photos = shownPhotos(area, photoSources);
  const devicePhotos = photoSources.onDevice.filter((p) => p.areaId === area.id);
  const areaPhoto = areaPhotoOf(photos);
  const needsAreaPhoto = !areaPhoto;
  const surfaces = readSurfaces(draft.hig_surfaces);

  function showFields(fields: Fields) {
    latestDraft.current = fields;
    setDraft(fields);
  }

  // Switching to another area: what's waiting is the previous area's, so it is saved now; show the new one's fields.
  useEffect(() => {
    showFields(area.fields);
    setActionError(null);
    return () => void saver.flush();
  }, [area.id]); // eslint-disable-line react-hooks/exhaustive-deps -- only on a different area, not on our own saves

  // A newer copy arrived from CRM (sync, or the refresh when a job opens) — the
  // etag moved. Show it, unless an edit is still waiting to be saved; saving
  // the old screen over it would undo someone else's change.
  useEffect(() => {
    if (!saver.pending()) showFields(area.fields);
  }, [area.etag]); // eslint-disable-line react-hooks/exhaustive-deps -- only when CRM's version changes
  // A save still waiting when the app is minimised or closed is written then (useDeferredSave): iOS can end a minimised app without warning (OFFLINE.md test 2).

  /** A keystroke still waiting to save is dropped: the area is going. */
  async function remove() {
    saver.cancel();
    await onRemove();
  }

  /** The copy takes the last keystrokes: they are saved first (leaving a text box saves it, in the same queue). */
  async function duplicate() {
    setActionError(null);
    await saver.flush();
    try {
      await onDuplicate();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    }
  }

  /** Before a conflict is resolved: a keystroke still waiting is saved (sending mine) or dropped (taking CRM's). */
  async function settleTyping(keep: boolean) {
    if (keep) await saver.flush();
    else saver.cancel();
  }

  function change(column: string, value: string, when: 'pause' | 'now' = 'pause') {
    return applyChanges({ [column]: value === '' && column !== 'hig_name' ? null : value }, when);
  }

  /** A text box has already waited for the pause, so its save is written now. */
  const saveText = (column: string) => (value: string) => change(column, value, 'now');

  function changeSurfaces(doc: SurfacesDoc, measuredHours: number) {
    applyChanges({ hig_surfaces: serialiseSurfaces(doc), hig_measuredhours: storedHours(measuredHours) });
  }

  function changeAssessment(doc: SurfacesDoc, assessedHours: number | null) {
    applyChanges({ hig_surfaces: serialiseSurfaces(doc), hig_assessedhours: assessedHours === null ? null : storedHours(assessedHours) });
    if (assessedHours !== null) onAssessed();
  }

  /** With 'now', resolves once written, so a text box's save counts as under way until it is. */
  function applyChanges(changes: Fields, when: 'pause' | 'now' = 'pause'): Promise<void> | undefined {
    showFields({ ...latestDraft.current, ...changes });
    saver.schedule({ id: area.id, changes });
    return when === 'now' ? saver.flush() : undefined;
  }

  return (
    <div className="space-y-4 rounded-lg border border-slate-200 bg-white p-4">
      {area.state === 'conflict' && !area.refusedReason && <ConflictChoices area={area} onResolve={settleTyping} />}
      <div className="flex flex-wrap items-center justify-end gap-2">
        <div className="flex flex-wrap items-center gap-3">
          <button type="button" onClick={() => void duplicate()} className="flex min-h-11 items-center gap-1 text-sm text-slate-600">
            <Copy className="h-4 w-4" /> Duplicate area
          </button>
          <RemoveArea area={area} photos={devicePhotos} onRemove={remove} onError={setActionError} />
        </div>
      </div>
      {actionError && <ErrorMessage>{actionError}</ErrorMessage>}
      {/* Every tab shows the flag (#437); the tile itself is on the Measure tab. */}
      {mode === 'assess' && needsAreaPhoto && <AreaPhotoFlag />}
      {mode === 'assess' ? (
        'doc' in surfaces ? (
          <AssessPanel doc={surfaces.doc} areaFields={draft} washOnly={siteWash(draft).washOnly} notes={text(draft.hig_assessnotes)} onChange={changeAssessment} onNotes={saveText('hig_assessnotes')} />
        ) : (
          <Notice>{surfaces.error} Update the app to see and edit them.</Notice>
        )
      ) : (
        <>
          {/* The feature photo first (#437): a perspective shot of the whole area. Required, but it blocks nothing. */}
          <div className="flex flex-wrap items-start gap-x-4 gap-y-3">
            <div className="min-w-48 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <Label htmlFor="area-name">Area name</Label>
                {needsAreaPhoto && <AreaPhotoFlag />}
              </div>
              <SavedTextField id="area-name" label="Area name" value={text(draft.hig_name)} onSave={saveText('hig_name')} className="mt-1" />
              {needsAreaPhoto && <p className="mt-2 text-sm text-slate-600">Start with the feature photo: a perspective shot of the whole area.</p>}
            </div>
            <AreaPhotoTile jobId={area.jobId} areaId={area.id} photo={areaPhoto} />
          </div>
          {/* Which building it is in; changing it moves the area (shown once the job has more than one). */}
          <AreaBuilding area={area} records={photoSources.records} />
          <div>
            <Label>Side</Label>
            <div className="mt-1 flex gap-2">
              {SIDES.map((side) => (
                <button
                  key={side}
                  type="button"
                  onClick={() => change('hig_side', side)}
                  className={`rounded-md border px-3 py-1.5 text-sm ${draft.hig_side === side ? 'border-primary bg-primary text-primary-foreground' : 'border-slate-300 bg-white text-slate-700'}`}
                >
                  {side}
                </button>
              ))}
            </div>
          </div>
          {/* Site facts the price uses for every option (PRICING.md); the desk's Take Off shows and changes the same values. */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Down time %</Label>
              <NumberField value={num(draft.hig_downtimepercent)} onChange={(v) => applyChanges({ hig_downtimepercent: v })} label="Down time %" placeholder="0" className="mt-1" />
              <p className="mt-1 text-xs text-slate-500">Hours lost to delays, ladder footing, masking</p>
            </div>
            <div>
              <Label>Abseil %</Label>
              <NumberField value={num(draft.hig_abseilpercent)} onChange={(v) => applyChanges({ hig_abseilpercent: v })} label="Abseil %" placeholder="0" className="mt-1" />
              <p className="mt-1 text-xs text-slate-500">Share of the area&rsquo;s hours worked on ropes</p>
            </div>
          </div>
          <div>
            <Label>Surfaces</Label>
            <div className="mt-2">
              {'doc' in surfaces ? (
                <SurfaceList
                  doc={surfaces.doc}
                  side={typeof draft.hig_side === 'string' ? draft.hig_side : null}
                  photos={photos}
                  jobId={area.jobId}
                  areaId={area.id}
                  onChange={changeSurfaces}
                />
              ) : (
                <Notice>{surfaces.error} Update the app to see and edit them.</Notice>
              )}
            </div>
          </div>
          <div>
            <Label htmlFor="area-scope">Scope notes</Label>
            <SavedTextField id="area-scope" label="Scope notes" value={text(draft.hig_scopenotes)} onSave={saveText('hig_scopenotes')} className="mt-1" rows={3} />
          </div>
          <div>
            <Label>Access, safety and preliminaries</Label>
            <div className="mt-2">
              <SiteRequirements area={area} side={typeof draft.hig_side === 'string' ? draft.hig_side : null} />
            </div>
          </div>
          <div>
            <Label htmlFor="area-exclusions">Exclusions</Label>
            <SavedTextField id="area-exclusions" label="Exclusions" value={text(draft.hig_exclusions)} onSave={saveText('hig_exclusions')} placeholder="What this area's scope leaves out" className="mt-1" rows={2} />
          </div>
          <div>
            <Label htmlFor="area-defects">Defects / remediation</Label>
            <SavedTextField id="area-defects" label="Defects / remediation" value={text(draft.hig_defectsnote)} onSave={saveText('hig_defectsnote')} placeholder="Damage or repairs to deal with before painting" className="mt-1" rows={2} />
          </div>
          <div>
            <Label htmlFor="area-access">Access notes</Label>
            <SavedTextField id="area-access" label="Access notes" value={text(draft.hig_accessneeds)} onSave={saveText('hig_accessneeds')} className="mt-1" rows={2} />
          </div>
          <div>
            <Label>General photos</Label>
            <p className="text-xs text-slate-500">Photos of one surface are taken on that surface, above.</p>
            <div className="mt-2">
              <PhotoStrip jobId={area.jobId} areaId={area.id} surface={null} photos={photos.filter((p) => !p.surfaceId && p.id !== areaPhoto?.id)} />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
